import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import _ from 'lodash';
import { animateScroll as scroll } from 'react-scroll';
import { useIntl } from 'react-intl';

import {
  showConfirmationModal,
} from '../../../actions/App';
import {
  fetchFileComments,
  fetchFileVersionComments,
  fetchShareFileComments,
  fetchShareFileVersionComments,
} from '../../../actions/Files';

import { isLoggedIn } from '../../../api/Auth';
import {
  APIcreateFileComment,
  APIcreateShareFileComment,
  APIdeleteFileComment,
  APIdeleteShareFileComment,
  APIupdateFileComment,
  APIupdateShareFileComment,
} from '../../../api/Files';

import FileComment from '../Comment';
import FileCommentForm from '../CommentForm';

import {
  getVisitorNameFromCookie,
  setVisitorNameCookie,
} from '../../../helpers/Auth';
import {
  addFlashMessage,
  addFlashErrorMessage,
} from '../../../helpers/FlashMessages';
import { defaultVideoCommentDuration } from '../../../helpers/Comments';

const FileViewTip = ({
  file,
  fileVersion,
  loadComments,
  loadShareComments,
  share,
  type,
  meta,
  highlight,
  openConfirmationModal,
  hideTipAndSelection,
  onOpen,
  createCommentCallback,
  updateCommentsCount,
  handleSidebarTab,
  readOnly,
}) => {
  const intl = useIntl();

  const [compact, setCompact] = useState(true);
  const [comment, setComment] = useState('');
  const [commentDuration, setCommentDuration] = useState(defaultVideoCommentDuration);
  const [name, setName] = useState(getVisitorNameFromCookie());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [parentId, setParentId] = useState(null);
  const inputRef = React.useRef(null);
  const [showReplies, setShowReplies] = useState(false);
  const [highlightObject, setHighlightObject] = useState(null);
  const commentInputRef = useRef(null);
  const replyInputRef = useRef(null);

  useEffect(() => {
    setCompact(true);
    if (highlight) {
      setHighlightObject(highlight);
      if (!(type === 'pdf' && highlight.id === 'new')) {
        setCompact(false);
      }
    }
  }, [highlight, type]);

  useEffect(() => {
    if (parentId) {
      scroll.scrollToBottom({
        containerId: `file-comments-body-${type}`,
      });
      if (replyInputRef.current) {
        replyInputRef.current.focus();
      }
    }
  }, [parentId, type]);

  useEffect(() => {
    onOpen();
  }, [onOpen]);

  const getMeta = () => {
    if (type === 'video') {
      return {
        ...meta,
        duration: commentDuration,
      };
    }
    return meta;
  };

  const createComment = () => {
    if (share) {
      APIcreateShareFileComment(share.uuid, file.uuid, {
        name,
        comment,
        parent_id: parentId,
        type,
        meta: getMeta(),
      }).then(({ data }) => {
        if (!data.parent_id) {
          setHighlightObject(data);
        } else {
          setHighlightObject({
            ...highlightObject,
            replies: [...highlightObject.replies, data],
          });
        }
        createCommentCallback(data);
        loadShareComments(share.uuid, file.uuid, fileVersion, true).then(() => {
          setComment('');
          setParentId(null);
          setSaving(false);
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your comment has been added' }),
          });
        });
      }).catch(() => {
        addFlashErrorMessage(intl);
        setSaving(false);
      });
    } else {
      APIcreateFileComment(file.uuid, {
        comment,
        type,
        meta: getMeta(),
        parent_id: parentId,
        file_version_id: fileVersion,
      }).then(({ data }) => {
        if (!data.parent_id) {
          setHighlightObject(data);
        } else {
          setHighlightObject({
            ...highlightObject,
            replies: [...highlightObject.replies, data],
          });
        }
        createCommentCallback(data);
        loadComments(file.uuid, fileVersion, true).then((commentsResponse) => {
          setComment('');
          setParentId(null);
          setSaving(false);
          updateCommentsCount(commentsResponse.comments_count, fileVersion);
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your comment has been added' }),
          });
        });
      }).catch(() => {
        addFlashErrorMessage(intl);
        setSaving(false);
      });
    }
  };

  const updateComment = () => {
    const commentInfo = {
      id: editId,
    };

    if (comment.length > 0) {
      commentInfo.comment = comment;
    }

    if (type === 'video') {
      commentInfo.meta = {
        ...highlightObject.meta,
        duration: commentDuration,
      };
    }

    if (share) {
      APIupdateShareFileComment(share.uuid, file.uuid, commentInfo).then(({ data }) => {
        if (!data.parent_id) {
          setHighlightObject({
            ...highlightObject,
            comment: data.comment,
            replies: data.replies,
            updated_at: data.updated_at,
          });
        } else {
          const newReplies = highlightObject.replies.map(r => {
            const reply = r;
            if (reply.id === data.id) {
              reply.comment = data.comment;
              reply.updated_at = data.updated_at;
            }
            return reply;
          });
          setHighlightObject({
            ...highlightObject,
            replies: newReplies,
          });
        }
        loadShareComments(share.uuid, file.uuid, fileVersion, true).then(() => {
          setComment('');
          setEditId(null);
          setSaving(false);
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your changes have been saved' }),
          });
        });
      }).catch(() => {
        addFlashErrorMessage(intl);
        setSaving(false);
      });
    } else {
      APIupdateFileComment(commentInfo).then(({ data }) => {
        if (!data.parent_id) {
          setHighlightObject({
            ...highlightObject,
            comment: data.comment,
            replies: data.replies,
            updated_at: data.updated_at,
          });
        } else {
          const newReplies = highlightObject.replies.map(r => {
            const reply = r;
            if (reply.id === data.id) {
              reply.comment = data.comment;
              reply.updated_at = data.updated_at;
            }
            return reply;
          });
          setHighlightObject({
            ...highlightObject,
            replies: newReplies,
          });
        }
        loadComments(file.uuid, fileVersion, true).then((commentsResponse) => {
          setComment('');
          setEditId(null);
          setSaving(false);
          updateCommentsCount(commentsResponse.comments_count, fileVersion);
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your changes have been saved' }),
          });
        });
      }).catch(() => {
        addFlashErrorMessage(intl);
        setSaving(false);
      });
    }
  };

  const handlePostComment = () => {
    const localErrors = {};
    if (!isLoggedIn() && (!name || name.length === 0)) {
      localErrors.name = true;
    }
    if (comment.length === 0 && !editId) {
      localErrors.comment = true;
    }
    if (type === 'video' && Number(commentDuration) === 0) {
      localErrors.commentDuration = true;
    }
    setErrors(localErrors);
    if (Object.keys(localErrors).length > 0) {
      return;
    }

    setSaving(true);

    if (name) {
      setVisitorNameCookie(name);
    }

    if (editId) {
      updateComment();
    } else {
      createComment();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handlePostComment();
    }
  };

  const handleNameChange = (e) => {
    const { value } = e.target;
    setName(value);
  };

  const handleCommentChange = (valueOrEvent) => {
    const value = typeof valueOrEvent === 'string' ? valueOrEvent : valueOrEvent?.target?.value;
    setComment(value || '');
  };

  const handleCommentDurationChange = (e) => {
    const { value } = e.target;
    if (!_.isNaN(Number(value))) {
      setCommentDuration(Number(value));
    }
  };

  const handleEdit = (id) => {
    setEditId(id);
  };

  const deleteComment = (commentId) => {
    if (share) {
      return APIdeleteShareFileComment(share.uuid, file.uuid, commentId).then(() => (
        loadShareComments(share.uuid, file.uuid, fileVersion, true).then(() => {
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'The comment has been deleted' }),
          });
        })
      )).catch(() => {
        addFlashErrorMessage(intl);
      });
    }

    return APIdeleteFileComment(commentId).then(() => (
      loadComments(file.uuid, fileVersion, true).then((commentsResponse) => {
        updateCommentsCount(commentsResponse.comments_count, fileVersion);
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The comment has been deleted' }),
        });
      })
    )).catch(() => {
      addFlashErrorMessage(intl);
    });
  };

  const handleDeleteComment = (commentId) => {
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Do you want to delete this comment?' }),
      onConfirm: () => (
        deleteComment(commentId)
      ),
    };
    openConfirmationModal(modalParams);
    hideTipAndSelection();
  };

  const handleParentId = (id) => {
    setParentId(id);
    if (id === parentId) {
      scroll.scrollToBottom({
        containerId: `file-comments-body-${type}`,
      });
      if (replyInputRef.current) {
        replyInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    if (!compact && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [compact]);

  if (compact) {
    return (
      <div className={`${type}-comment-tip Tip`}>
        <Button color="primary" onClick={() => setCompact(false)} size="sm">
          {intl.formatMessage({ defaultMessage: 'Add comment' })}
        </Button>
      </div>
    );
  }

  if (highlightObject && !(type === 'pdf' && highlightObject.id === 'new')) {
    return (
      <div className="file-comments-body-wrapper" id={`file-comments-body-${type}`}>
        <div className="file-comments-body file-comments-body-compact">
          <FileComment
            comment={highlightObject}
            setScrollTo={() => {}}
            setParent={handleParentId}
            setEdit={handleEdit}
            handleDeleteComment={handleDeleteComment}
            handleCommentChange={handleCommentChange}
            handleCommentDurationChange={handleCommentDurationChange}
            handleKeyPress={handleKeyPress}
            handleNameChange={handleNameChange}
            handlePostComment={handlePostComment}
            highlighted={false}
            inputRefFunc={(ref) => { inputRef.current = ref; }}
            saving={saving}
            deleting={false}
            isEdit={editId === highlightObject.id}
            alias={type}
            handleSidebarTab={handleSidebarTab}
            readOnly={readOnly}
          />

          {highlightObject.replies.length > 0 && !showReplies && (
            <div className="file-comments-show-replies">
              <Button color="primary" size="sm" onClick={() => setShowReplies(true)}>
                {intl.formatMessage({
                  defaultMessage: 'Show {count} {count, plural, one {reply} other {replies}} here',
                }, { count: highlightObject.replies.length })}
              </Button>
            </div>
          )}
          {showReplies && [...highlightObject.replies].map(r => (
            <FileComment
              key={r.id}
              isReply={true}
              comment={r}
              setScrollTo={() => {}}
              setParent={handleParentId}
              setEdit={handleEdit}
              handleDeleteComment={handleDeleteComment}
              handleCommentChange={handleCommentChange}
              handleKeyPress={handleKeyPress}
              handleNameChange={handleNameChange}
              handlePostComment={handlePostComment}
              highlighted={false}
              inputRefFunc={(ref) => { replyInputRef.current = ref; }}
              saving={saving}
              deleting={false}
              isEdit={editId === r.id}
              alias={type}
              handleSidebarTab={handleSidebarTab}
              readOnly={readOnly}
            />
          ))}
          {(!readOnly && (parentId === highlightObject.id)) && (
            <FileCommentForm
              commentId={comment.id}
              isReply={true}
              commentValue={comment}
              name={name}
              saving={saving}
              errors={errors}
              inputRefFunc={(ref) => { replyInputRef.current = ref; }}
              handleKeyPress={handleKeyPress}
              handleNameChange={handleNameChange}
              handleCommentChange={handleCommentChange}
              handlePostComment={handlePostComment}
            />
          )}
        </div>
      </div>
    );
  }

  if (readOnly) return null;

  return (
    <div className={`${type}-comment-tip`}>
      <FileCommentForm
        commentValue={comment}
        errors={{}}
        saving={saving}
        isReply={false}
        inputRefFunc={(ref) => { commentInputRef.current = ref; }}
        handleKeyPress={handleKeyPress}
        handleNameChange={handleNameChange}
        handleCommentChange={handleCommentChange}
        handleCommentDurationChange={handleCommentDurationChange}
        handlePostComment={handlePostComment}
        commentDuration={commentDuration}
        name={name}
        type={type}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  share: state.Shares.share,
  commentsAnnotateMode: state.Files.commentsAnnotateMode,
});

const mapDispatchToProps = dispatch => ({
  loadComments: (fileUuid, fileVersion = null, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchFileVersionComments(fileUuid, fileVersion, silent));
    }
    return dispatch(fetchFileComments(fileUuid, silent));
  },
  loadShareComments: (shareUuid, fileUuid, fileVersion, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchShareFileVersionComments(shareUuid, fileUuid, fileVersion, silent));
    }
    return dispatch(fetchShareFileComments(shareUuid, fileUuid, silent));
  },
  openConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
});

FileViewTip.propTypes = {
  file: PropTypes.object.isRequired,
  highlight: PropTypes.object,
  share: PropTypes.object,
  meta: PropTypes.object,
  loadComments: PropTypes.func.isRequired,
  loadShareComments: PropTypes.func.isRequired,
  openConfirmationModal: PropTypes.func.isRequired,
  hideTipAndSelection: PropTypes.func,
  type: PropTypes.string.isRequired,
  createCommentCallback: PropTypes.func,
  onOpen: PropTypes.func,
  fileVersion: PropTypes.number,
  updateCommentsCount: PropTypes.func.isRequired,
  handleSidebarTab: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

FileViewTip.defaultProps = {
  share: null,
  meta: null,
  highlight: null,
  fileVersion: null,
  readOnly: false,
  hideTipAndSelection: () => {},
  createCommentCallback: () => {},
  onOpen: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(FileViewTip);
